import React, { FC } from 'react';
import { observer } from 'mobx-react-lite';
import { Platform } from '../../models/enums/platform';
import { Badge, Button } from 'react-bootstrap';
import { useYakofficeUserIdToken } from '@yakoffice/yakoffice-firebase';
import { useParams } from 'react-router-dom';
import styles from './buildArtefact.module.sass';
import { IBuild } from '../../models/Build/Builld';
import { IArtefact } from '../../models/Artefact/Artefact';

interface PropsType {
    build: IBuild;
    os: string;
}

export const BuildArtefacts: FC<PropsType> = observer((props) => {

    const token = useYakofficeUserIdToken();
    const routeParams = useParams<{ projectId: string }>();

    const arrangeArtefactsByEnvironment = (artefacts: IArtefact[]) => {

        return {
            'Development': artefacts.filter(artefact => artefact.environment && artefact.environment === 'dev'),
            'Production': artefacts.filter(artefact => artefact.environment && artefact.environment === 'prd'),
            'Qas': artefacts.filter(artefact => artefact.environment && artefact.environment === 'qas'),
            'Other': artefacts.filter(artefact => artefact.environment ? artefact.environment !== 'dev' && artefact.environment !== 'prd' && artefact.environment !== 'qas' : artefact)
        };
    };

    const handleDownload = async (artefact: IArtefact) => {

        try {
            const downloadUrl = await artefact.getDownloadUrl(props.os, routeParams.projectId, props.build.id, token);

            const link = document.createElement('a');
            link.href = downloadUrl;

            link.setAttribute('download', artefact.id);

            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);

        } catch (error) {
            console.error('Download failed:', error);
        }
    };

    const artefactButton = (artefact: IArtefact) => {

        const artefactIconClass = (artefact.platform === Platform.android) ? `fa-android` : `fa-apple`;

        return (
            <div key={artefact.id} className={styles.artefactButtonWrapper}>
                <Button onClick={() => handleDownload(artefact)}
                        className={`${styles.artefactButton} ${artefact.platform}`}
                        size="lg"
                        variant={artefact.buildConfig === 'release' ? 'secondary' : 'outline-secondary'}>
          <span className={styles.offHover}><i
              className={`fab ${styles.deviceIcon} ${artefactIconClass} ${artefact.platform}`} />{artefact.buildConfig}</span>
                    <span className={styles.onHover}><i
                        className="fas fa-download" />{artefact.readableSize()}</span>
                </Button>
            </div>
        );
    };


    const renderArtefacts = (build: IBuild) => {

        let artefacts = build.allArtefacts().sort((a, b) => a.buildConfig.localeCompare(b.buildConfig));

        if (props.os === 'ios')
            artefacts = build.iosArtefacts().sort((a, b) => a.buildConfig.localeCompare(b.buildConfig));

        else if (props.os === 'android')
            artefacts = build.androidArtefacts().sort((a, b) => a.buildConfig.localeCompare(b.buildConfig));

        const arrangedArtefacts = arrangeArtefactsByEnvironment(artefacts);

        return (
            <div>
                {arrangedArtefacts.Development.length > 0 &&
                    <div className={styles.artefactGroup}>
                        <div className={styles.groupTitle}>Development</div>
                        <div
                            className={styles.artefacts}>{arrangedArtefacts.Development.map((artefact) => artefactButton(artefact))}</div>
                    </div>
                }
                {arrangedArtefacts.Production.length > 0 &&
                    <div className={styles.artefactGroup}>
                        <div className={styles.groupTitle}>Production <Badge bg="danger"><i
                            className="fas fa-rocket ms-1 me-1" /></Badge>
                        </div>
                        <div
                            className={styles.artefacts}>{arrangedArtefacts.Production.map(artefact => artefactButton(artefact))}</div>
                    </div>
                }
                {arrangedArtefacts.Qas.length > 0 &&
                    <div className={styles.artefactGroup}>
                        <div className={styles.groupTitle}>QAS</div>
                        <div
                            className={styles.artefacts}>{arrangedArtefacts.Qas.map(artefact => artefactButton(artefact))}</div>
                    </div>
                }
                {arrangedArtefacts.Other.length > 0 &&
                    <div className={styles.artefactGroup}>
                        <div className={styles.groupTitle}>Other</div>
                        <div
                            className={styles.artefacts}>{arrangedArtefacts.Other.map(artefact => artefactButton(artefact))}</div>
                    </div>
                }
            </div>
        );
    };

    return (
        <div className={styles.artefactsWrapper}>
            {renderArtefacts(props.build)}
        </div>
    );
});
